import { PaletteOptions, createTheme } from "@mui/material";

import { colors } from "./colors";

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: colors.dark.primary_midnight, // primary-midnight
      light: colors.dark.text_primary, // text-primary
      dark: colors.dark.locked_primary, // locked-primary
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    secondary: {
      main: colors.dark.secondary_guardian, // --chat-bot-theme-secondary-guardian
      light: colors.dark.input_guardian_light, // input-guardian-light
      dark: colors.dark.button_tonal_guardian, // button-tonal-guardian
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    tertiary: {
      main: colors.dark.tertiary_nautical, // --chat-bot-theme-tertiary-nautical
    },
    error: {
      main: colors.dark.error_text, // --chat-bot-theme-error-text
      light: colors.dark.error_container, // --chat-bot-theme-error-container
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    warning: {
      main: colors.dark.warning_text, // --chat-bot-theme-warning-text
      light: colors.dark.warning_container, // --chat-bot-theme-warning-container
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    info: {
      main: colors.dark.info_text, // --chat-bot-theme-info-text
      light: colors.dark.info_container, // --chat-bot-theme-info-container
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    success: {
      main: colors.dark.success_text, // --chat-bot-theme-success-text
      light: colors.dark.success_container, // --chat-bot-theme-success-container
      contrastText: colors.dark.text_primary_light, // text-primary-light
    },
    text: {
      primary: colors.dark.text_primary, // --chat-bot-theme-text-primary
      secondary: colors.dark.text_secondary, // --chat-bot-theme-text-secondary
      disabled: colors.dark.text_muted, // --chat-bot-theme-text-muted
    },
    background: {
      default: colors.dark.bgoffwhitecool, // --chat-bot-theme-bgoffwhitecool
      paper: colors.dark.bgoffwhiteneutral, // --chat-bot-theme-bgoffwhiteneutral
    },
    divider: colors.dark.divider_line_pale_gray, // --chat-bot-theme-divider-line-pale-gray

    // Custom colors
    accent: {
      main: colors.dark.accent_horizon, // accent-horizon
      light: colors.dark.coolactive, // coolactive
    },
    slate: {
      main: colors.dark.slate_main, // slate
      light: colors.dark.slate_light, // text-primary-slate
    },
    grey: {
      50: colors.dark.bgoffwhitewarm, // --chat-bot-theme-bgoffwhitewarm
      100: colors.dark.bgoffwhitebluetint, // --chat-bot-theme-bgoffwhitebluetint
      200: colors.dark.divider_line_soft_white, // --chat-bot-theme-divider-line-soft-white
      300: colors.dark.divider_line_pale_gray, // --chat-bot-theme-divider-line-pale-gray
      400: colors.dark.divider_line_light_gray, // --chat-bot-theme-divider-line-light-gray
      500: colors.dark.light_grey, // --chat-bot-theme-light-grey
      600: colors.dark.light_charcoal, // --chat-bot-theme-light-charcoal
      700: colors.dark.soft_grey, // --chat-bot-theme-soft-grey
      800: colors.dark.charcoal, // --chat-bot-theme-charcoal
      900: colors.dark.jet_black, // --chat-bot-theme-jet-black
    },
    action: {
      active: colors.dark.coolactive, // --chat-bot-theme-coolactive
      hover: colors.dark.coolactive_hover,
      selected: colors.dark.selected, // --chat-bot-theme-selected
      disabled: colors.dark.disabled, // --chat-bot-theme-disabled
      disabledBackground: colors.dark.disabledBackground, // --chat-bot-theme-disabledBackground
    },
    // Custom colors not part of MUI standard palette
    custom: {
      // Brand colors
      brandPrimary: colors.dark.brand_primary,           // #e50c20 - Main brand red
      brandPrimaryDark: colors.dark.brand_primary_dark,  // #b8091a - Darker red for hover
      brandPrimaryLight: colors.dark.brand_primary_light, // #ff4757 - Lighter red
      brandPrimarySubtle: colors.dark.brand_primary_subtle, // #2d0a0e - Dark red tint
      
      // Additional backgrounds
      backgroundPrimary: colors.dark.background_primary,
      backgroundSecondary: colors.dark.background_secondary,
      backgroundTertiary: colors.dark.background_tertiary,
      
      // Text colors
      textOnRed: colors.dark.text_on_red,
      
      // Slate colors
      slateMain: colors.dark.slate_main,
      slateLight: colors.dark.slate_light,
      slateDark: colors.dark.slate_dark,
      
      // Legacy support
      accent: {
        horizon: colors.dark.accent_horizon,
      },
      heroBlue: colors.dark.hero_blue,
      placeholderGray: colors.dark.placeholder_gray,
      glacial: colors.dark.natural_light_BG_glacial,
      buttonTonalGuardian: colors.dark.button_tonal_guardian,
      inputGuardianLight: colors.dark.input_guardian_light,
      lockedPrimary: colors.dark.custom_locked_primary,
      constantBlack: colors.dark.constant_black,
    },
  } as PaletteOptions,
  typography: {
    fontFamily: "'Poppins', Helvetica",
    h1: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "48px",
      fontWeight: 700,
      lineHeight: "56px",
      letterSpacing: "0px",
    },
    h2: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "36px",
      fontWeight: 700,
      lineHeight: "44px",
      letterSpacing: "0px",
    },
    h3: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "28px",
      fontWeight: 500,
      lineHeight: "36px",
      letterSpacing: "0px",
    },
    h4: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "24px",
      fontWeight: 500,
      lineHeight: "32px",
      letterSpacing: "0px",
    },
    h5: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "18px",
      fontWeight: 500,
      lineHeight: "26px",
      letterSpacing: "0px",
    },
    body1: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px",
      letterSpacing: "0px",
    },
    body2: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
      letterSpacing: "0px",
    },
    caption: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "12px",
      letterSpacing: "0px",
    },
    button: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "0.014000000208616257px",
      textTransform: "none",
    },
    subtitle1: {
      fontFamily: "'Poppins', Helvetica",
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "24px",
      letterSpacing: "0.01600000023841858px",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "rgba(239, 242, 245, 1)", // --chat-bot-theme-bgoffwhitecool
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
        },
        containedPrimary: {
          background:
            "linear-gradient(90deg, rgba(134,97,252,1) 0%, rgba(7,221,216,1) 100%)",
          color: "rgba(244, 255, 255, 1)", // --chat-bot-theme-natural-light-BG-glacial
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(247, 249, 251, 1)", // --chat-bot-theme-card-background
          borderRadius: "8px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(240, 242, 247, 1)", // --chat-bot-theme-divider-line-pale-gray
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2,
        }),
        head: ({ theme }) => ({
          ...theme.typography.subtitle1,
          fontWeight: 500,
        }),
        body: ({ theme }) => ({
          ...theme.typography.body1,
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          ...theme.typography.h5,
        }),
        secondary: ({ theme }) => ({
          ...theme.typography.body2,
          color: theme.palette.text.secondary,
        }),
      },
    },
  },
});
